import { Pipe, PipeTransform } from '@angular/core';

import { DateService } from '../../services/date.service';
import { TimestampFormatService, TimestampType } from '../../services/timestamp-format.service';
import { LocService } from '../../shared-context/loc.service';

@Pipe({
    name: 'responsiveRelativeDate'
})
export class ResponsiveRelativeDatePipe implements PipeTransform {
    constructor(
        private readonly dateService: DateService,
        private readonly locService: LocService,
        private readonly timestampFormatService: TimestampFormatService) {
    }

    public transform(dateFrom: Date, shortDate: boolean, fallbackText?: string, inline?: boolean) {
        if (!dateFrom) {
            return '';
        }

        if (fallbackText === null || typeof fallbackText === 'undefined') {
            fallbackText = dateFrom.toDateString();
        }

        return this.commonFormat(dateFrom, shortDate, fallbackText, inline);
    }

    private commonFormat(dateFrom: Date, shortDate: boolean, fallbackText: string, inline: boolean): string {
        if (!this.locService.supportsLocaleSpecifiers) {
            return fallbackText;
        }

        const timespan = this.dateService.timespanInSeconds(dateFrom);
        switch (this.timestampFormatService.getTimestampType(dateFrom)) {
            case TimestampType.InMinutes:
                return this.timespanString(timespan, this.locService.NumberMinutesInShort, -60);
            case TimestampType.None:
                return '';
            case TimestampType.JustNow:
                return inline ? this.locService.JustNowLowerCase : this.locService.JustNow;
            case TimestampType.LessThanOneHour:
                return this.timespanString(timespan, this.locService.NumberMinutesAgoShort, 60);
            case TimestampType.Today:
                return this.timespanString(timespan, this.locService.NumberHoursAgoShort, 3600);
            case TimestampType.Yesterday:
                if (shortDate) {
                    return inline ? this.locService.YesterdayLowerCase : this.locService.Yesterday;
                }

                const formatString = inline ? this.locService.TimeYesterdayLowerCase : this.locService.TimeYesterday;
                return this.locService.format(formatString, this.locService.formatTimeFromDate(dateFrom));
            case TimestampType.ThisWeek:
                if (shortDate) {
                    return this.locService.localizedWeekday(dateFrom);
                } else {
                    return this.locService.format(this.locService.TimeDayOfWeek, this.locService.localizedWeekday(dateFrom), this.locService.formatTimeFromDate(dateFrom));
                }
            case TimestampType.ThisYear:
                return this.locService.localizedMonthDate(dateFrom);
            default:
                return this.locService.localizedDateStringMedium(dateFrom);
        }
    }

    private timespanString(timespan: number, locString: string, periodInSeconds: number): string {
        const displayNumber = Math.round(timespan / periodInSeconds);
        return this.locService.format(locString, this.locService.localizedNumberString(displayNumber));
    }
}
